<script setup lang="ts">
  import { balanceFormat } from '@/helpers/converter'
  import { lazyLoadComponent } from '@/helpers/lazyLoadComponent'
  import { useGAEventStore } from '@/store/GAEventTracking'
  import { useLightBoxStore } from '@/store/LightBox'
  import { useMMStore } from '@/store/MarvelMachine'
  import { useUserStore } from '@/store/User'
  import { watchEffect } from 'vue'
  const MarvelGamePopup = lazyLoadComponent({
    comp: () => import('@/components/MarvelMachine/MarvelGamePopup.vue'),
  })
  const HelpMessagePopup = lazyLoadComponent({
    comp: () => import('@/components/MarvelMachine/HelpMessagePopup.vue'),
  })
  const helpMessagePopupDefaultSetting = {
    items: [{ component: HelpMessagePopup }],
    option: { class: 'marvel-machine-helpMessage-lightbox' },
  }
  const userStore = useUserStore()
  const mmStore = useMMStore()
  const lbStore = useLightBoxStore()
  const gaEvent = useGAEventStore()
  watchEffect(() => {
    if (mmStore.user.spinBalance) {
      const $marvelBalance = document.querySelector('.marvel-machine-main-play__usable-marvel-balance')
      $marvelBalance?.classList.add('updated')
      setTimeout(() => {
        $marvelBalance?.classList.remove('updated')
      }, 200)
    }
  })
  function basicCheck(next: () => void) {
    if (mmStore.user.isUserBannedCountry) {
      return
    }
    if (!userStore.isLoggedIn) {
      window.nexon.sso.login()
      return
    }
    if (!mmStore.marvelMachine.event.enableSpin) {
      lbStore.openLightBox({
        ...helpMessagePopupDefaultSetting,
        componentProp: {
          content: 'noPlay',
        },
      })
      return
    }
    if (!mmStore.user.isCharacterAvailable) {
      lbStore.openLightBox({
        ...helpMessagePopupDefaultSetting,
        componentProp: {
          content: 'noCharacter',
        },
      })
      return
    }
    next()
  }
  const runSpin = () => {
    gaEvent.pushActionEvent({
      interaction: 'button_click',
      sectionNumber: mmStore.game.resultOn ? 2 : 1,
      sectionName: mmStore.game.resultOn ? 'After Tap!' : 'Marvel Machine',
      sectionContent: `Tap-${userStore.isLoggedIn ? 'Logged in' : 'Logged out'}`,
    })
    basicCheck(() => {
      mmStore.runSpin()
    })
  }
  const runDoubleSpin = () => {
    gaEvent.pushActionEvent({
      interaction: 'button_click',
      sectionNumber: 2,
      sectionName: 'After Tap!',
      sectionContent: `Double`,
    })
    basicCheck(() => {
      if (mmStore.game.resultItems && !mmStore.game.isDoubleRunning) {
        mmStore.getUserNxBalance(() => {
          if (mmStore.user.nxBalance < mmStore.marvelMachine.doubleMePrice) {
            lbStore.openLightBox({
              ...helpMessagePopupDefaultSetting,
              componentProp: {
                content: 'noNX',
              },
            })
          } else {
            mmStore.runDoubleSpin()
          }
        })
      }
    })
  }
  const buyMarvel = () => {
    gaEvent.pushActionEvent({
      interaction: 'button_click',
      sectionNumber: mmStore.game.resultOn ? 2 : 1,
      sectionName: mmStore.game.resultOn ? 'After Tap!' : 'Marvel Machine',
      sectionContent: `Buy Marvel`,
    })
    basicCheck(() => {
      if (!mmStore.marvelMachine.event.enableSpinPurchase) {
        lbStore.openLightBox({
          ...helpMessagePopupDefaultSetting,
          componentProp: {
            content: 'noEvent',
          },
        })
        return
      }
      lbStore.openLightBox({
        items: [{ component: MarvelGamePopup }],
        componentProp: { content: 'buyMarvel' },
        option: { class: 'marvel-machine-game-lightbox' },
      })
    })
  }
</script>

<template>
  <div
    class="marvel-machine-main-play"
    :class="{
      inactive: mmStore.isGameBlockedOrUnabledSpin,
    }"
  >
    <div class="marvel-machine-main-play__rest-wrapper">
      <div class="marvel-machine-main-play__usable-marvel font1">
        <div class="marvel-machine-main-play__usable-marvel-balance">
          {{ mmStore.user.spinBalance > 0 ? balanceFormat(mmStore.user.spinBalance) : '-' }}
        </div>
        <div class="marvel-machine-main-play__usable-marvel-text">Usable Marvel</div>
      </div>
      <div
        class="marvel-machine-main-play__tap-btn"
        :class="{
          inactive: mmStore.isGameBlockedOrUnabledSpin || !mmStore.game.isReadyToSpin || mmStore.user.spinBalance === 0,
        }"
        @click="runSpin()"
      ></div>
      <div
        class="marvel-machine-main-play__buy-marvel-btn"
        :class="{
          inactive: mmStore.isGameBlockedOrUnabledSpin || !mmStore.marvelMachine.event.enableSpinPurchase,
        }"
        @click="buyMarvel()"
      ></div>
    </div>
    <div
      class="marvel-machine-main-play__double-btn"
      :class="{
        showDouble: mmStore.game.resultOn && !mmStore.game.isGameBeingResetted,
        inactiveDouble: mmStore.game.isDoubleRunning,
        hideDouble: mmStore.game.doubleResultOn,
      }"
    >
      <div class="marvel-machine-main-play__double-btn-clickpart" @click="runDoubleSpin()"></div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .marvel-machine-main-play {
    position: relative;
    z-index: 10;
    &.inactive {
      .marvel-machine-main-play__rest-wrapper {
        background-image: url('@/assets/imgs/mavelmachine/marvelMainTapButton/ui-bg-inactive.png');
      }
      .marvel-machine-main-play__usable-marvel {
        opacity: 0.5;
      }
    }
  }

  .marvel-machine-main-play__usable-marvel {
    color: white;
    font-weight: bold;
    position: absolute;
    text-align: center;
    left: calc(29px * var(--marvel-desktopRatio));
    top: calc(32px * var(--marvel-desktopRatio));
    width: calc(156px * var(--marvel-desktopRatio));
    height: calc(50px * var(--marvel-desktopRatio));
    color: #cffbef;
    text-transform: uppercase;
  }
  .marvel-machine-main-play__usable-marvel-text {
    font-size: calc(13px * var(--marvel-desktopRatio));
  }
  .marvel-machine-main-play__usable-marvel-balance {
    line-height: 0.65em;
    font-size: calc(37px * var(--marvel-desktopRatio));
    margin-bottom: calc(8px * var(--marvel-desktopRatio));
    transform: scale(1);
    transition: transform 0.3s, color 0.3s;
    color: white;

    &.updated {
      transform: scale(1.1);
      color: #ff7030;
    }
  }

  .marvel-machine-main-play__buy-marvel-btn {
    position: absolute;
    width: calc(222px * var(--marvel-desktopRatio));
    height: calc(74px * var(--marvel-desktopRatio));
    right: calc(14px * var(--marvel-desktopRatio));
    top: calc(14px * var(--marvel-desktopRatio));
    cursor: pointer;
    background: url('@/assets/imgs/mavelmachine/marvelMainTapButton/buy-marvel.png') no-repeat center center / 100% auto;
    transition: background 0.3s;
    &:hover:not(.inactive) {
      background-image: url('@/assets/imgs/mavelmachine/marvelMainTapButton/buy-marvel-hover.png');
    }
    &.inactive {
      opacity: 0.5;
      cursor: auto;
    }
  }
  .marvel-machine-main-play__rest-wrapper {
    width: calc(647px * var(--marvel-desktopRatio));
    height: calc(110px * var(--marvel-desktopRatio));
    background: url('@/assets/imgs/mavelmachine/marvelMainTapButton/ui-bg.png') no-repeat center center / 100% auto;
    z-index: 2;
    position: relative;
  }
  .marvel-machine-main-play__tap-btn {
    width: calc(283px * var(--marvel-desktopRatio));
    height: calc(122px * var(--marvel-desktopRatio));
    background: url('@/assets/imgs/mavelmachine/marvelMainTapButton/tap-btn.png') no-repeat center center / 100% auto;
    position: absolute;
    left: 50%;
    top: 50%;
    z-index: 1;
    transform: translate(-50%, -50%);
    transition: background 0.3s;
    cursor: pointer;
    &:hover,
    &:after {
      content: '';
      background-image: url('@/assets/imgs/mavelmachine/marvelMainTapButton/tap-btn-hover.png');
    }
    &.inactive {
      cursor: auto;
      content: '';
      background-image: url('@/assets/imgs/mavelmachine/marvelMainTapButton/tap-btn-disabled.png');
    }
  }

  .marvel-machine-main-play__double-btn {
    width: calc(460px * var(--marvel-desktopRatio));
    height: calc(99px * var(--marvel-desktopRatio));
    z-index: 1;
    background: url('@/assets/imgs/mavelmachine/marvelMainTapButton/double-it-btn.png') no-repeat center center / 100%
      auto;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    transition: background 0.3s, transform 0.3s, opacity 0.3s;
    opacity: 0;

    &:hover {
      background-image: url('@/assets/imgs/mavelmachine/marvelMainTapButton/double-it-btn-hover.png');
    }
    &.showDouble {
      transform: translate(-50%, -154%);
      opacity: 1;
    }

    &.inactiveDouble {
      transform: translate(-50%, -154%);
      opacity: 0.7;
    }
    &.hideDouble {
      transform: translate(-50%, -50%);
      opacity: 0;
    }
  }

  .marvel-machine-main-play__double-btn-clickpart {
    width: calc(200px * var(--marvel-desktopRatio));
    height: calc(86px * var(--marvel-desktopRatio));
    position: absolute;
    top: calc(3px * var(--marvel-desktopRatio));
    left: 50%;
    transform: translateX(-50%);
    cursor: pointer;
  }

  /* @media #{$tabletForMarvel} {
  .marvel-machine-main-play__double-btn {
    width: 35vw;
    height: 9vw;
  }
  .marvel-machine-main-play__usable-marvel {
    width: 20vw;
    height: 7vw;
    left: 0.8vw;
    top: 3.4vw;
  }
  .marvel-machine-main-play__usable-marvel-balance {
    font-size: 4vw;
    margin-bottom: 1vw;
  }
  .marvel-machine-main-play__usable-marvel-text {
    font-size: 1.2vw;
  }
  .marvel-machine-main-play__rest-wrapper {
    width: 67vw;
    height: 12vw;
  }
  .marvel-machine-main-play__tap-btn {
    width: 28vw;
    height: 12vw;
  }
  .marvel-machine-main-play__buy-marvel-btn {
    width: 24vw;
    height: 8.4vw;
    top: 1.5vw;
    right: 1.5vw;
  }
} */

  @media #{$mobileForMarvel} {
    .marvel-machine-main-play__double-btn {
      width: 51vw;
      height: 12.1vw;
    }
    .marvel-machine-main-play__double-btn-clickpart {
      width: 20vw;
      height: 9vw;
      top: 1.2vw;
    }
    .marvel-machine-main-play__usable-marvel {
      width: 31vw;
      height: 11vw;
      left: 0.5vw;
      top: 3.7vw;
    }
    .marvel-machine-main-play__usable-marvel-balance {
      font-size: 5vw;
    }
    .marvel-machine-main-play__usable-marvel-text {
      font-size: 1.8vw;
    }
    .marvel-machine-main-play__rest-wrapper {
      width: 89vw;
      height: 15vw;
    }
    .marvel-machine-main-play__tap-btn {
      width: 37vw;
      height: 19vw;
    }
    .marvel-machine-main-play__buy-marvel-btn {
      width: 31vw;
      height: 11vw;
      top: 1.5vw;
      right: 1.5vw;
    }
  }
</style>
